import { lazy, Suspense } from 'react';
import { Spinner } from '../shared/components/loader/Loader';

// Loading component for suspense fallback
export const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <Spinner />
  </div>
);

// HOC to create lazy loaded components with suspense
export const lazyLoad = (importFunc: () => Promise<any>, exportName?: string) => {
  const LazyComponent = lazy(async () => {
    if (exportName) {
      const module = await importFunc();
      return { default: module[exportName] };
    }
    return importFunc();
  });
  
  return (props: any) => (
    <Suspense fallback={<LoadingFallback />}>
      <LazyComponent {...props} />
    </Suspense>
  );
};

// Lazily load Auth components
export const LazyLoginForm = lazyLoad(() => import('@/features/auth/pages'), 'LoginForm');
export const LazyRegisterForm = lazyLoad(() => import('@/features/auth/pages'), 'RegisterForm');
export const LazyEmailVerification = lazyLoad(() => import('@/features/auth/pages/email-verification/EmailVerificationPage'),'EmailVerificationPage');
export const LazyForgotPassword = lazyLoad(() => import('@/features/auth/pages/forgot-password/ForgotPassword'), 'ForgotPasswordPage');
export const LazyResetPassword = lazyLoad(() => import('@/features/auth/pages/forgot-password/ResetPassword'), 'ResetPasswordPage');

// Lazily load Dashboard components
export const LazyDashboard = lazyLoad(() => import('@/features/dashboard/pages/dashboard/Dashboard'), 'Dashboard');
export const LazyInitialDashboard = lazyLoad(() => import('@/features/dashboard/pages/initial-dashboard/InitialDashboard'), 'InitialDashboard');

// Lazily load Alerts
export const LazyAlerts = lazyLoad(() => import('@/features/alerts/pages'), 'Alerts');

// Lazily load Repository components
export const LazyApps = lazyLoad(() => import('@/features/repository/pages/apps/Apps'), 'Apps');
export const LazyFirmware = lazyLoad(() => import('@/features/repository/pages/firmware/Firmware'), 'Firmware');
export const LazyKeyManagement = lazyLoad(() => import('@/features/repository/pages/key-management/KeyManagement'), 'KeyManagement');
export const LazyTaskAutomation = lazyLoad(() => import('@/features/repository/pages/task-automation/TaskAutomation'), 'TaskAutomation');

// Lazily load Management components
export const LazyDevices = lazyLoad(() => import('@/features/management'), 'Devices');
export const LazyAddDevice = lazyLoad(() => import('@/features/management'), 'AddDevice');
export const LazyDeviceGroups = lazyLoad(() => import('@/features/management/pages/device-groups/DeviceGroups'), 'DeviceGroups');
export const LazyTemplates = lazyLoad(() => import('@/features/management/pages/templates/Templates'), 'Templates');
export const LazySchedules = lazyLoad(() => import('@/features/management/pages/schedules/Schedules'), 'Schedules');
export const DeviceDashboard = lazyLoad(() => import('@/features/device-dashboard/pages/DeviceDashboard'));

// Lazily load System components
export const LazyGeneral = lazyLoad(() => import('@/features/system/pages/general/General'), 'General');
export const LazyClients = lazyLoad(() => import('@/features/system/pages/clients/Clients'), 'Clients');
export const LazyProjects = lazyLoad(() => import('@/features/system/pages/projects/Projects'), 'Projects');
export const LazyLocation = lazyLoad(() => import('@/features/system/pages/location/Location'), 'Locations');
export const LazyUsers = lazyLoad(() => import('@/features/users/pages/user-management/UserManagement'), 'UserManagement');
export const LazyRolesProfiles = lazyLoad(() => import('@/features/system/pages/roles-profiles/RolesProfiles'), 'RolesProfiles');

// Lazily load Integrations
export const LazyIntegrations = lazyLoad(() => import('@/features/integrations/pages'), 'Integrations');
export const LazyProfiles = lazyLoad(() => import('@/features/profile'), 'Profile');

// Lazily load Logs and Reports
export const LazyLogs = lazyLoad(() => import('@/features/logs/pages'), 'Logs');
export const LazyReports = lazyLoad(() => import('@/features/reports/pages'), 'Reports');

export const LazyPrivacyPolicy = lazyLoad(() => import ('@/features/footer/PrivacyPolicy'),'PrivacyPolicy');
export const LazyTermsofService = lazyLoad(() => import ('@features/footer/TermsOfService'),'TermsOfService');
export const LazyContactUs = lazyLoad(() => import('@/features/footer/ContactUs'), 'ContactUs');

// aakash tracker
// export const LazyAakashTracker = lazyLoad(() => import('@/features/tracker/AakashTracker'));

export const ProjectDashboard = lazyLoad(() => import('@/features/project-management/pages/ProjectManagement'),'ProjectDashboard');
